import type { DimenzijaZaPrikaz, VrednostDimenzije } from '@koda/shared';
import { useEffect, useRef, useState, type JSX } from 'react';

import { useBesedila, useJezik } from '../lib/jezik';

/**
 * Izbira v filtru po dimenziji.
 *
 * Izbranih vrednosti je lahko vec (npr. dva projekta skupaj), `brez` pa doda
 * knjizbe, ki dimenzije nimajo - v seznamu »(prazno)«. Oboje je mogoce
 * izbrati hkrati.
 */
export interface IzbiraDimenzije {
  koda: string;
  vrednosti: string[];
  brez: boolean;
}

/** Ali izbira sploh kaj omejuje. */
export function jePrazna(izbira: IzbiraDimenzije | null): boolean {
  return izbira === null || (izbira.vrednosti.length === 0 && !izbira.brez);
}

/**
 * Naziv vrednosti, kadar ga izvoz ima.
 *
 * Pantheon izvaza samo sifre, Minimax tudi nazive. Naziv, ki je enak sifri,
 * ne pove nicesar, zato se izpusti.
 */
function nazivVrednosti(v: VrednostDimenzije): string {
  return v.naziv !== null && v.naziv !== '' && v.naziv !== v.vrednost ? v.naziv : '';
}

/** Sifra, pod katero v seznamu nastopa »(prazno)«; ni vrednost iz izvoza. */
const PRAZNO = Symbol('prazno');

/**
 * Ena vrstica seznama: sifra, za njo naziv.
 *
 * Sifre so kratke in priblizno enako dolge, zato se v svojem stolpcu poravnajo
 * in so nazivi berljivi drug pod drugim.
 */
type Postavka = { kljuc: string | typeof PRAZNO; sifra: string; naziv: string; knjizb: number };

/**
 * Izbira dimenzije nad poročilom (oddelek, stroškovni nosilec, analitika).
 *
 * Posnema filter vrtilne tabele v Excelu, ker je računovodjem domač: gumb s
 * trenutno izbiro, v oknu pa iskanje, »(Vse)«, »(prazno)« in seznam vrednosti,
 * spodaj pa stikalo *Izberi več elementov* ter *V redu* / *Prekliči*. Dokler
 * okno ni potrjeno, se poročilo ne preračunava — klik po vsaki kljukici bi
 * pomenil novo poizvedbo.
 *
 * Seznam ni v kodi: pride iz uvoženih knjižb tega podjetja in leta, števila pa
 * se nanašajo na odprto poročilo (docs/ARHITEKTURA.md, 6.4).
 */
export function IzbirnikDimenzije({
  dimenzije,
  izbrana,
  naIzbiro,
}: {
  dimenzije: DimenzijaZaPrikaz[];
  izbrana: IzbiraDimenzije | null;
  naIzbiro: (izbira: IzbiraDimenzije | null) => void;
}): JSX.Element | null {
  const b = useBesedila();
  const jezik = useJezik();
  const [odprt, setOdprt] = useState(false);
  const [osnutek, setOsnutek] = useState<IzbiraDimenzije | null>(null);
  const [iskanje, setIskanje] = useState('');
  const [vecElementov, setVecElementov] = useState(false);
  const okvir = useRef<HTMLDivElement>(null);

  // Klik zunaj okna in tipka Esc zapreta okno brez potrditve, enako kot v
  // Excelu: nepotrjena izbira ne sme nepričakovano spremeniti poročila.
  useEffect(() => {
    if (!odprt) return undefined;
    const zapri = (d: MouseEvent): void => {
      if (okvir.current && !okvir.current.contains(d.target as Node)) setOdprt(false);
    };
    const naTipko = (d: KeyboardEvent): void => {
      if (d.key === 'Escape') setOdprt(false);
    };
    document.addEventListener('mousedown', zapri);
    document.addEventListener('keydown', naTipko);
    return () => {
      document.removeEventListener('mousedown', zapri);
      document.removeEventListener('keydown', naTipko);
    };
  }, [odprt]);

  if (dimenzije.length === 0) return null;

  const trenutna = izbrana === null ? null : (dimenzije.find((d) => d.koda === izbrana.koda) ?? null);

  /** Napis na gumbu, enak kot v Excelu: (Vse), ena vrednost ali (Več elementov). */
  function napisGumba(): string {
    if (trenutna === null || izbrana === null || jePrazna(izbrana)) return b.vse;
    const koliko = izbrana.vrednosti.length + (izbrana.brez ? 1 : 0);
    if (koliko > 1) return b.vecElementov;
    if (izbrana.brez) return b.prazno;
    const ena = trenutna.vrednosti.find((v) => v.vrednost === izbrana.vrednosti[0]);
    if (ena === undefined) return izbrana.vrednosti[0] ?? b.vse;
    const naziv = nazivVrednosti(ena);
    return naziv === '' ? ena.vrednost : `${ena.vrednost} ${naziv}`;
  }

  function odpri(): void {
    if (izbrana === null) return;
    setOsnutek({ ...izbrana, vrednosti: [...izbrana.vrednosti] });
    setVecElementov(izbrana.vrednosti.length + (izbrana.brez ? 1 : 0) > 1);
    setIskanje('');
    setOdprt(true);
  }

  const postavke: Postavka[] =
    trenutna === null
      ? []
      : [
          ...trenutna.vrednosti.map((v) => ({
            kljuc: v.vrednost,
            sifra: v.vrednost,
            naziv: nazivVrednosti(v),
            knjizb: v.knjizb,
          })),
          { kljuc: PRAZNO, sifra: b.prazno, naziv: '', knjizb: trenutna.brez },
        ];
  const krajevno = jezik === 'en' ? 'en-GB' : 'sl-SI';
  const iskano = iskanje.trim().toLocaleLowerCase(krajevno);
  // Iska se po sifri in po nazivu: uporabnik se enega ali drugega spomni.
  const vidne =
    iskano === ''
      ? postavke
      : postavke.filter((p) => `${p.sifra} ${p.naziv}`.toLocaleLowerCase(krajevno).includes(iskano));

  const jeIzbran = (p: Postavka): boolean =>
    osnutek === null ? false : p.kljuc === PRAZNO ? osnutek.brez : osnutek.vrednosti.includes(p.kljuc);

  /** Kljukica pri eni postavki; brez stikala je izbira ena sama, kot v Excelu. */
  function preklopi(p: Postavka): void {
    if (osnutek === null) return;
    if (!vecElementov) {
      setOsnutek(
        p.kljuc === PRAZNO
          ? { ...osnutek, vrednosti: [], brez: true }
          : { ...osnutek, vrednosti: [p.kljuc], brez: false },
      );
      return;
    }
    if (p.kljuc === PRAZNO) {
      setOsnutek({ ...osnutek, brez: !osnutek.brez });
      return;
    }
    const kljuc = p.kljuc;
    setOsnutek({
      ...osnutek,
      vrednosti: osnutek.vrednosti.includes(kljuc)
        ? osnutek.vrednosti.filter((v) => v !== kljuc)
        : [...osnutek.vrednosti, kljuc],
    });
  }

  const vseVidneIzbrane = vidne.length > 0 && vidne.every(jeIzbran);
  const nekajIzbranih = vidne.some(jeIzbran);

  /** »(Vse)«: prvi klik označi vse vidne, drugi jih odznači. */
  function preklopiVse(): void {
    if (osnutek === null) return;
    if (vseVidneIzbrane) {
      const odvzeti = new Set(vidne.filter((p) => p.kljuc !== PRAZNO).map((p) => String(p.kljuc)));
      setOsnutek({
        ...osnutek,
        vrednosti: osnutek.vrednosti.filter((v) => !odvzeti.has(v)),
        brez: vidne.some((p) => p.kljuc === PRAZNO) ? false : osnutek.brez,
      });
      return;
    }
    const dodani = vidne.filter((p) => p.kljuc !== PRAZNO).map((p) => String(p.kljuc));
    setOsnutek({
      ...osnutek,
      vrednosti: [...new Set([...osnutek.vrednosti, ...dodani])],
      brez: vidne.some((p) => p.kljuc === PRAZNO) ? true : osnutek.brez,
    });
  }

  function potrdi(): void {
    setOdprt(false);
    if (osnutek === null) return;
    // Vse izbrano je isto kot brez filtra; naslov strani naj tega ne nosi.
    const vse = postavke.length > 0 && postavke.every(jeIzbran);
    naIzbiro(jePrazna(osnutek) || vse ? { ...osnutek, vrednosti: [], brez: false } : osnutek);
  }

  return (
    <div className="izbirnik-dimenzije">
      <label className="drobno">
        {b.dimenzija}
        <select
          value={izbrana?.koda ?? ''}
          onChange={(d) => {
            const koda = d.target.value;
            // Ob menjavi dimenzije izbira vrednosti ne sme ostati od prejšnje.
            setOdprt(false);
            naIzbiro(koda === '' ? null : { koda, vrednosti: [], brez: false });
          }}
        >
          <option value="">{b.brezFiltra}</option>
          {dimenzije.map((d) => (
            <option key={d.koda} value={d.koda}>
              {d.naziv}
            </option>
          ))}
        </select>
      </label>

      {trenutna !== null && (
        <div className="spustni" ref={okvir}>
          <span className="drobno">{b.vrednost}</span>
          <button
            type="button"
            className="spustni-gumb"
            aria-expanded={odprt}
            aria-haspopup="dialog"
            onClick={() => {
              if (odprt) setOdprt(false);
              else odpri();
            }}
          >
            <span>{napisGumba()}</span>
            <span aria-hidden="true">▾</span>
          </button>

          {odprt && osnutek !== null && (
            <div className="spustni-panel" role="dialog" aria-label={b.filterZa(trenutna.naziv)}>
              <input
                type="search"
                className="iskanje"
                placeholder={b.isci}
                value={iskanje}
                onChange={(d) => {
                  setIskanje(d.target.value);
                }}
              />

              <ul className="seznam-vrednosti">
                <li className="vse">
                  <label>
                    <input
                      type="checkbox"
                      checked={vseVidneIzbrane}
                      ref={(el) => {
                        if (el) el.indeterminate = !vseVidneIzbrane && nekajIzbranih;
                      }}
                      onChange={preklopiVse}
                    />
                    <span>{b.vse}</span>
                  </label>
                </li>
                {vidne.map((p) => (
                  <li key={p.kljuc === PRAZNO ? 'prazno' : p.kljuc}>
                    <label>
                      <input
                        type={vecElementov ? 'checkbox' : 'radio'}
                        name="vrednost-dimenzije"
                        checked={jeIzbran(p)}
                        onChange={() => {
                          preklopi(p);
                        }}
                      />
                      <span className="sifra">{p.sifra}</span>
                      <span className="ime" title={p.naziv}>
                        {p.naziv}
                      </span>
                      <span className="drobno">{p.knjizb.toLocaleString(krajevno)}</span>
                    </label>
                  </li>
                ))}
                {vidne.length === 0 && <li className="drobno">{b.niZadetkov}</li>}
              </ul>

              <label className="vec-elementov">
                <input
                  type="checkbox"
                  checked={vecElementov}
                  onChange={(d) => {
                    setVecElementov(d.target.checked);
                  }}
                />
                <span>{b.izberiVecElementov}</span>
              </label>

              <div className="spustni-gumbi">
                <button type="button" className="gumb" onClick={potrdi}>
                  {b.vRedu}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOdprt(false);
                  }}
                >
                  {b.preklici}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
